import { AccountInfo, Configuration } from '@azure/msal-browser';
import {
  AuthEventCallback,
  IAuthService,
  LoginProviderCallback,
  LoginProviderType,
  LoginResult,
} from '@common/services/auth/auth.service.interface';
import { UserRole } from '@user/constants/user.constant';
import { User } from '@user/models/user.model';
import { isObjectTypeValid } from '@util/utils/object.util';
import { jwtDecode } from 'jwt-decode';
import noop from 'lodash/noop';

type TokenPayload = {
  extension_Roles?: string;
};

export default abstract class BasicAuthService implements IAuthService {
  auth: Awaited<ReturnType<typeof this.initializeAuth>> | undefined;

  scopes: string[] = [];

  initializerPromise: Promise<unknown> | undefined;

  constructor() {
    this.initialize = this.initialize.bind(this);
    this.getUserInfo = this.getUserInfo.bind(this);
    this.loginProvider = this.loginProvider.bind(this);
    this.logout = this.logout.bind(this);
    this.getToken = this.getToken.bind(this);
    this.subscribeEvent = this.subscribeEvent.bind(this);
    this.subscribeLoginProvider = this.subscribeLoginProvider.bind(this);
  }

  // NOTE: User mapper function
  // eslint-disable-next-line class-methods-use-this
  protected mapAccountToUser(account: AccountInfo, token?: string): User | null {
    let role: UserRole | undefined;
    if (
      account?.idTokenClaims &&
      isObjectTypeValid<TokenPayload>(account.idTokenClaims, 'extension_Roles')
    ) {
      role = Object.values(UserRole).find(
        (item) => item === account?.idTokenClaims?.extension_Roles,
      );
    }

    if (!role) {
      try {
        const payload = token ? jwtDecode<TokenPayload>(token) : null;
        if (payload?.extension_Roles) {
          const roles = Object.values(UserRole);
          role = roles.find((item) => item === payload?.extension_Roles);
        }
      } catch {
        // Do nothing
      }
    }

    return { name: account?.name ?? '', role, username: account?.username ?? '' };
  }

  abstract getAuthConfigurations(): Promise<Configuration>;

  protected async initializeAuth() {
    try {
      const { PublicClientApplication } = await import('@azure/msal-browser');

      const configurations = await this.getAuthConfigurations();
      const instance = new PublicClientApplication({
        ...configurations,
        cache: { cacheLocation: 'localStorage', ...configurations.cache },
      });

      this.auth = instance;

      await this.auth.initialize();

      return instance;
    } catch (error) {
      return await this.handleError(error);
    }
  }

  async initialize() {
    if (this.auth) return;
    if (!this.initializerPromise) this.initializerPromise = this.initializeAuth();

    await this.initializerPromise;
  }

  // eslint-disable-next-line class-methods-use-this
  protected async handleError(error: unknown): Promise<never | null> {
    throw error;
  }

  protected async getTokenFromAccount(account: AccountInfo | null): Promise<string | null> {
    if (!this.auth) return null;

    const tokenRequest = { account: account ?? undefined, scopes: this.scopes };

    try {
      const response = await this.auth.acquireTokenSilent(tokenRequest);
      if (!response) return null;

      return response.accessToken;
    } catch (error) {
      return null;
    }
  }

  async getUserInfo(): Promise<User | null> {
    await this.initialize();

    if (!this.auth) return null;

    const account = this.auth.getActiveAccount();
    if (!account) return null;

    return this.mapAccountToUser(account);
  }

  async loginProvider(loginType?: LoginProviderType): Promise<LoginResult | null> {
    await this.initialize();

    if (!this.auth) return null;

    try {
      // Logout if there is an account
      if (this.auth.getActiveAccount()) await this.logout(loginType);
    } catch {
      // Do nothing
    }

    const loginRequest = { prompt: 'login', scopes: this.scopes };

    try {
      switch (loginType) {
        case LoginProviderType.Redirect: {
          await this.auth.loginRedirect(loginRequest);
          break;
        }

        case LoginProviderType.PopUp:
        default: {
          const response = await this.auth.loginPopup(loginRequest);
          this.auth.setActiveAccount(response.account);

          let token: string | null = response.accessToken;
          if (!token) token = await this.getTokenFromAccount(response.account);
          if (token) return { token, user: this.mapAccountToUser(response.account, token) };

          break;
        }
      }
    } catch (error) {
      return await this.handleError(error);
    }

    return null;
  }

  async logout(loginType?: LoginProviderType): Promise<void> {
    await this.initialize();

    if (!this.auth) return;

    try {
      switch (loginType) {
        case LoginProviderType.Redirect: {
          await this.auth.logoutRedirect();
          break;
        }

        case LoginProviderType.PopUp:
        default: {
          await this.auth.logoutPopup();
          break;
        }
      }
    } catch (error) {
      await this.handleError(error);
    }

    this.auth.setActiveAccount(null);
    await this.auth.clearCache();
  }

  async getToken(): Promise<string | null> {
    await this.initialize();

    if (!this.auth) return null;

    const account = this.auth.getActiveAccount();
    if (!account) return null;

    return this.getTokenFromAccount(account);
  }

  subscribeEvent(callback: AuthEventCallback): VoidFunction {
    if (!this.auth) return noop;

    const callbackId = this.auth.addEventCallback((message) =>
      callback(message.eventType, message.payload),
    );

    return () => {
      if (!this.auth) return;
      if (callbackId) this.auth.removeEventCallback(callbackId);
    };
  }

  async subscribeLoginProvider(callback: LoginProviderCallback): Promise<void> {
    await this.initialize();

    if (!this.auth) return noop();

    return this.auth.handleRedirectPromise().then(async (response) => {
      const user = response?.account
        ? this.mapAccountToUser(response?.account, response?.accessToken)
        : await this.getUserInfo();
      const token = response?.accessToken ?? (await this.getToken());

      callback(!user || !token ? null : { token, user });
    });
  }
}
